import React, { useEffect, useRef } from "react";
import BottomNavigation from "@material-ui/core/BottomNavigation";
import BottomNavigationAction from "@material-ui/core/BottomNavigationAction";
import { Zoom } from "@material-ui/core";
import { useIntl } from "react-intl";
import ProductListViewSelect from "./ProductListViewSelect";
import SortProducts from "./SortProducts";
import PinnedProductsSelect from "./PinnedProductsSelect";
import { ProductSequenceData } from "../store/product-list/ProductListTypes";
import { Tooltip } from "@mui/material";
import { styled, Box } from "@mui/material";
import { ColorManagementToolbarIcon, SmartSortIcon } from "assets";
import SmartSort from "./SmartSort";
import { useSelector } from "react-redux";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import BulkLeadingColorMgmt from "./BulkLeadingColorMgmt";
import { selectProductColors } from "store/product/ProductSelectors";
import { getProductsHavingColors } from "utils/ProductUtil";
import {
  selectIsPreviewMode,
  selectSavingCategoryIds,
} from "store/product-list/ProductListSelectors";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import {
  selectAccountFeatures,
  selectCallerAccountId,
} from "store/app-config/AppConfigSelectors";
import AppState from "store/AppState";

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
  leftMargin: number;
}
interface Props {
  products: ProductSequenceData[];
  currentPageProductIds: string[];
}

const StyledContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
}));

const StyledRoot = styled(BottomNavigation)(({ theme }) => ({
  height: "fit-content",
  backgroundColor: "transparent",
  "& .MuiBottomNavigationAction-wrapper": {
    fontSize: "1.5em",
    padding: "5px",
    borderRadius: "5px",
    width: "100%",
  },
  "& .MuiBottomNavigationAction-root": {
    display: "flex",
    minWidth: "32px",
    height: "auto",
    padding: "0",
    marginRight: "7px",
    "&:hover": {
      borderRadius: "5px",
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
    },
  },
  "& .MuiBottomNavigationAction-root.Mui-selected": {
    borderColor: theme.palette.primaryHoverColor?.main,
    color: "#f18308",
    borderRadius: "5px",
    backgroundColor: theme.palette.primaryHoverColor?.hoverLight,
    "&:hover": {
      borderColor: theme.palette.primaryHoverColor?.main,
      color: "#f18308",
      borderRadius: "5px",
      backgroundColor: theme.palette.primaryHoverColor?.hoverLight,
    },
  },
  "& .MuiBottomNavigationAction-root.Mui-selected .MuiBottomNavigationAction-wrapper":
    {
      borderColor: theme.palette.primary.main,
    },
  "& .MuiBottomNavigationAction-root.Mui-disabled": {
    opacity: 0.3,
  },
}));

const StyledTooltip = styled(Tooltip)(({ theme }) => ({
  borderRadius: "5px !important",
  border: "0.5px solid",
  fontSize: "10px !important",
  padding: "3px 12px 4px 12px",
}));

function TabPanel(props: TabPanelProps) {
  const { children, value, index, leftMargin, ...other } = props;
  let styleProps: any = {
    marginLeft: `${leftMargin}px`,
    display: "inline-flex",
  };

  if (value !== index) styleProps = {};

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={styleProps}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

export default function ProductListGroupBtn(props: Props) {
  const intl = useIntl();
  // const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const isLeadingColorSupported = adapterInfo?.capabilities?.includes(
    "leading-color",
  ) as boolean;
  const productColorsData = useSelector(selectProductColors);
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const prevIsPreviewMode = useRef(isPreviewMode);
  const hasProducts = props?.products?.length ? true : false;
  const savingCategoryIds = useSelector(selectSavingCategoryIds);
  const categoryId = useSelector(selectCurrentCategory);
  const accountId = useSelector(selectCallerAccountId);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const isPinnedProductsFeatureEnabled = features?.["pinned-products"] ?? false;
  const isBulkPinnedProductsFeatureEnabled =
    features?.["bulk-pinned-products"] ?? false;
  const isCurrentCategorySaving = savingCategoryIds.includes(categoryId);
  // Reset toolbar selection to first icon (View) when exiting preview mode
  useEffect(() => {
    if (prevIsPreviewMode.current && !isPreviewMode && value !== 0) {
      setValue(0);
    }
    prevIsPreviewMode.current = isPreviewMode;
  }, [isPreviewMode, value]);

  // Check if any current page products have colors
  const hasProductsWithColors = getProductsHavingColors(
    props.currentPageProductIds,
    productColorsData,
  );

  return (
    <StyledContainer>
      <StyledRoot
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
        }}
        showLabels
        // className={classes.root}
      >
        <StyledTooltip
          // classes={{ tooltip: classes.tooltip }}
          TransitionComponent={Zoom}
          placement="top"
          title={intl.formatMessage({
            id: "productListGroupBtn.viewLabel",
            defaultMessage: "Change Product List View",
          })}
          arrow
        >
          <BottomNavigationAction
            icon={<i className="fas fa-th"></i>}
            disableRipple
          />
        </StyledTooltip>

        <StyledTooltip
          // classes={{ tooltip: classes.tooltip }}
          TransitionComponent={Zoom}
          placement="top"
          title={intl.formatMessage({
            id: "productListGroupBtn.sortLabel",
            defaultMessage: "Sort Products",
          })}
          arrow
        >
          <BottomNavigationAction
            disabled={!hasProducts || isPreviewMode || isCurrentCategorySaving}
            icon={<i className="fas fa-sort-amount-down"></i>}
            disableRipple
          />
        </StyledTooltip>

        <StyledTooltip
          TransitionComponent={Zoom}
          placement="top"
          title={intl.formatMessage({
            id: "productListGroupBtn.smartSort",
            defaultMessage: "Smart Sort",
          })}
          arrow
        >
          <BottomNavigationAction
            disabled={!hasProducts || isPreviewMode || isCurrentCategorySaving}
            icon={<SmartSortIcon />}
            disableRipple
          />
        </StyledTooltip>
        {isPinnedProductsFeatureEnabled &&
          isBulkPinnedProductsFeatureEnabled && (
            <StyledTooltip
              TransitionComponent={Zoom}
              placement="top"
              title={intl.formatMessage({
                id: "productListGroupBtn.pinProductsLabel",
                defaultMessage: "Pin Products",
              })}
              arrow
            >
              <BottomNavigationAction
                disabled={
                  !hasProducts || isPreviewMode || isCurrentCategorySaving
                }
                icon={<i className="fas fa-thumbtack"></i>}
                disableRipple
              />
            </StyledTooltip>
          )}
        {isLeadingColorSupported && (
          <StyledTooltip
            TransitionComponent={Zoom}
            placement="top"
            title={intl.formatMessage({
              id: "colorManagementModal.setLeadingColor",
              defaultMessage: "Set Leading Color",
            })}
            arrow
          >
            <BottomNavigationAction
              disabled={!hasProductsWithColors}
              icon={<ColorManagementToolbarIcon />}
              disableRipple
            />
          </StyledTooltip>
        )}
      </StyledRoot>
      <TabPanel value={value} index={0} leftMargin={100}>
        <ProductListViewSelect />
      </TabPanel>
      <TabPanel value={value} index={1} leftMargin={50}>
        <SortProducts products={props.products} />
      </TabPanel>
      <TabPanel value={value} index={2} leftMargin={42}>
        <SmartSort />
      </TabPanel>
      <TabPanel value={value} index={3} leftMargin={50}>
        <PinnedProductsSelect />
      </TabPanel>
      <TabPanel value={value} index={4} leftMargin={50}>
        {isLeadingColorSupported && hasProductsWithColors && (
          <BulkLeadingColorMgmt />
        )}
      </TabPanel>
    </StyledContainer>
  );
}
